/**
 * Vercel 主题提供者组件
 * 管理亮色/暗色/跟随系统主题切换，并应用到整个 admin/v1 应用
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

export type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * 获取系统主题偏好
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * 获取主题 context（必须在 VercelThemeProvider 内部使用）
 * @throws 如果不在 Provider 内部会抛出错误
 */
export const useVercelTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useVercelTheme 必须在 VercelThemeProvider 内部使用");
  }
  return context;
};

/**
 * 可选的主题 context hook（不会抛出错误）
 * @returns context 或 null（如果不在 Provider 内部）
 */
export const useVercelThemeOptional = () => {
  return useContext(ThemeContext);
};

interface VercelThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export const VercelThemeProvider: React.FC<VercelThemeProviderProps> = ({
  children,
  defaultTheme = "dark",
  storageKey = "vercel-theme",
}) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // 从 localStorage 读取保存的主题
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      if (stored === "light" || stored === "dark" || stored === "system") {
        return stored;
      }
    }
    return defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (typeof window === "undefined") return "light";
    const stored = localStorage.getItem(storageKey);
    if (stored === "light" || stored === "dark") return stored;
    return getSystemTheme();
  });

  // 应用主题到 DOM
  const applyTheme = useCallback((resolved: ResolvedTheme) => {
    const root = document.documentElement;

    // 移除旧的主题类
    root.classList.remove("theme-vercel-light", "theme-vercel-dark", "dark");

    // 添加新的主题类
    root.classList.add(`theme-vercel-${resolved}`);

    // 暗色模式时同时添加 .dark 类，以支持 shadcn 组件的 dark: 变体
    if (resolved === "dark") {
      root.classList.add("dark");
    }

    // 同时添加 admin-v1-vercel 类以应用全局样式
    root.classList.add("admin-v1-vercel");
  }, []);

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === "system") {
        const newResolved = e.matches ? "dark" : "light";
        setResolvedTheme(newResolved);
        applyTheme(newResolved);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, applyTheme]);

  // 主题变化时更新 resolvedTheme 并应用
  useEffect(() => {
    const resolved = theme === "system" ? getSystemTheme() : theme;
    setResolvedTheme(resolved);
    applyTheme(resolved);

    // 保存到 localStorage
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey, applyTheme]);

  const toggleTheme = () => {
    setThemeState((prev) => {
      if (prev === "light") return "dark";
      if (prev === "dark") return "system";
      return "light";
    });
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, toggleTheme, setTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
